import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

const PRESET_MS = {
  "24h": 24 * 60 * 60 * 1000,
  "7d": 7 * 24 * 60 * 60 * 1000,
  "30d": 30 * 24 * 60 * 60 * 1000,
};

function isPreset(value) {
  return (
    value === "24h" || value === "7d" || value === "30d" || value === "all"
  );
}

export function useWindow(defaultPreset = "all") {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPreset = searchParams.get("range");
  const preset = isPreset(urlPreset) ? urlPreset : defaultPreset;

  const [anchor, setAnchor] = useState(() => Date.now());

  const { from, to } = useMemo(() => {
    if (preset === "all") return { from: undefined, to: undefined };
    const now = anchor;
    return {
      from: new Date(now - PRESET_MS[preset]).toISOString(),
      to: new Date(now).toISOString(),
    };
  }, [preset, anchor]);

  const setPreset = useCallback(
    (next) => {
      setAnchor(Date.now());
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("range", next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const refresh = useCallback(() => setAnchor(Date.now()), []);

  const label = PRESET_LABELS[preset];

  return { preset, from, to, setPreset, refresh, label };
}

const PRESET_LABELS = {
  "24h": "Last 24 hours",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  all: "All time",
};
